import { Alignment, Api, CompanySettings, McLeodClassicIntegration, ModelRow, Navigation, StringUtil } from "@mcleod/core";
import {
    Button,
    Checkbox,
    ChangeEvent,
    ClickEvent,
    DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent,
    DataSourceValidationEvent, DropdownItem, DropdownSelectionEvent, EditRowDecorator, Layout,
    LookupModelSearchEvent,
    Panel, TableRow, TableRowCreationEvent, TableRowDisplayEvent,
    TableRowExpansionEvent, TableRowMoveEvent, Textbox,
    ValidationResult
} from "@mcleod/components";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { ModelCustomer } from "@mcleod/ar";
import { getDispatchControlBoolean } from "@mcleod/general";
import { AutogenLayoutTenderProfile } from "./autogen/AutogenLayoutTenderProfile";
import { handleCommIdSelection, setQuickInfo } from "./DataFusionUtil";
import { EdiCustomerSlideout } from "./EdiCustomerSlideout";
import { EDITemplateUpdateProps } from "./EDITemplateSlideout";
import { createCustomizationSlideout, handleButtonMasterBatchClick, handleMasterBatchCheck, setFuncAckTemplateValues, setReplyTemplateValues, setTemplateValues } from "./EdiUtility";
import { EdiReqdFldGrpExpanded } from "./EdiReqdFldGrpExpanded";
import { ModelEdiTemplate } from "./models/ModelEdiTemplate";
import { ModelEdiCarrier } from "./models/ModelEdiCarrier";
import { ChangeOptions } from "./ChangeOptions";
import { BaseProfile, QuickInfoMapping } from "./BaseProfile";
import { ModelEdiorderProfile } from "./models/ModelEdiorderProfile";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";
import { LookupModelSelectionEvent } from "@mcleod/components";
import { ModelCopyControl } from "@mcleod/dispatch/src/models/ModelCopyControl";

const layoutURL = "lme/datafusion/TenderProfile";
export class TenderProfile extends AutogenLayoutTenderProfile {
    private baseProfile: BaseProfile;

    private TENDER = "T";
    private BILL_OF_LADING = "L";
    private FUNCTIONAL_ACK = "F";
    private OUTBOUND = "O";
    private INBOUND = "I";
    private XML = "X";
    private JSON = "J";
    private _rulesNeedSaving: boolean = false;
    public static readonly WHEN_TO_SEND: DropdownItem[] = [
        new DropdownItem("NONE", "Never"),
        new DropdownItem("CRAS", "Carrier Assignment")
    ];
    public static readonly WHEN_TO_SEND_LTL: DropdownItem[] = [
        new DropdownItem("NONE", "Never"),
        new DropdownItem("CRAS", "Carrier Assignment"),
        new DropdownItem("DPCR", "Depart to Carrier")
    ];

    public get rulesNeedSaving(): boolean {
        return this._rulesNeedSaving;
    }
    public set rulesNeedSaving(value: boolean) {
        this._rulesNeedSaving = value;
    }

    override async onLoad() {
        // Initialize BaseProfile with required context
        this.baseProfile = new BaseProfile({
            mainDataSource: this.mainDataSource,
            textboxSendToCompany: this.textboxSendToCompany,
            textboxCommId: this.textboxCommId,
            tableRequiredField: this.tableRequiredField,
            INBOUND: this.INBOUND,
            OUTBOUND: this.OUTBOUND
        });

        await this.baseProfile.initializeIntercompanySupport();
        this.checkboxIntercompany.visible = this.baseProfile.isIcEnabled;
        const companySettings = CompanySettings.get();
        if (companySettings?.is_ltl) {
            if (getDispatchControlBoolean("ltl_calculate_service", false)) {
                this.checkboxOverrideWarning.visible = true;
            } else {
                this.checkboxOverrideWarning.visible = false;
            }
        } else {
            this.checkboxOverrideWarning.visible = false;
        }

        if (companySettings.is_brokerage && companySettings.is_asset) {
            this.textboxDefMoveType.visible = true;
        } else {
            this.textboxDefMoveType.visible = false;
        }

        this.textboxCustomer.lookupModelDisabled = true;
        this.textboxAutoSendTender.items = (companySettings?.is_ltl) ? TenderProfile.WHEN_TO_SEND_LTL : TenderProfile.WHEN_TO_SEND;
        this.setAutoSendTenderTooltip(this.textboxAutoSendTender, this.textboxAutoSendTender.selectedItem?.value);

        // Set up table required field handlers for Tender profiles using BaseProfile
        this.baseProfile.setupTableRequiredFieldHandlers("204", false, "Tender Data Validation Rules");
        this.mainDataSource.doBeforeDataSourcePost = async (): Promise<boolean> => {
            if (this.mainDataSource?.mode === DataSourceMode.ADD &&
                this.mainDataSource?.activeRow?.get("partner_id") &&
                this.mainDataSource?.activeRow?.get("alt_partner_id") &&
                this.mainDataSource?.activeRow?.get("version") &&
                this.mainDataSource?.activeRow?.get("company_id") &&
                this.mainDataSource?.activeRow?.get("direction")) {

                const validationResults: ValidationResult[] = this.validate(true, true);
                if (!ValidationUtil.checkValidationResults(validationResults)) {
                    ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid));
                    return false;
                }

                const partnerId = this.mainDataSource?.activeRow?.get("partner_id");
                const altPartnerId = this.mainDataSource?.activeRow?.get("alt_partner_id");
                const version = this.mainDataSource?.activeRow?.get("version");
                const companyId = this.mainDataSource?.activeRow?.get("company_id");
                const direction = this.mainDataSource?.activeRow?.get("direction");
                const transactionSet = this.mainDataSource?.activeRow?.get("transaction_set", "204");
                if (await this.profileExists(partnerId, altPartnerId, version, companyId, direction, transactionSet)) {
                    CommonDialogs.showError("Partner profile already exists");
                    return false;
                }
            }
            return true;
        };
    }

    public static navigateTo(recordId: string) {
        const url = layoutURL + `?mode=update&key=${recordId}`;
        Navigation.navigateTo(url, { newTab: true })
    }

    communicationsIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.commBeforeLookupModelSearch(event, this.TENDER);
    }

    funcAckCommunicationsIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.commBeforeLookupModelSearch(event, this.FUNCTIONAL_ACK);
    }

    templateIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.templateBeforeLookupModelSearch(event, this.TENDER, this.mainDataSource?.activeRow?.get("direction"));
    }

    funcAckTemplateIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.templateBeforeLookupModelSearch(event, this.FUNCTIONAL_ACK, this.OUTBOUND);
    }

    async templateIdOnLookupModelSearch(event: LookupModelSelectionEvent) {
        if (!StringUtil.isEmptyString(event.getValueFromSelectedRow("id"))) {
            const template = await new ModelEdiTemplate().searchSingle({ id: event.getValueFromSelectedRow("id") });
            if (template) {
                this.setFuncAckTab(template.get("format_type") !== this.XML && template.get("format_type") !== this.JSON);
                setTemplateValues(template, this.mainDataSource);
            }
        }
    }

    async funcAckTemplateIdOnLookupModelSearch(event: LookupModelSelectionEvent) {
        if (!StringUtil.isEmptyString(event.getValueFromSelectedRow("id"))) {
            const template = await new ModelEdiTemplate().searchSingle({ id: event.getValueFromSelectedRow("id") });
            if (template) {
                setFuncAckTemplateValues(template, this.mainDataSource);
            }
        }
    }

    async afterExecution(event: DataSourceExecutionEvent) {
        this.tabsetTenderProfile.visible = this.mainDataSource?.mode !== DataSourceMode.ADD;
        await this.setQuickInfos();
        await this.loadTemplateData();
        await this.updateUIVisibility();
        this.setNextBatchFields();
        if (event && event.getAction() === DataSourceAction.UPDATE && this.mainDataSource?.activeRow?.get("template_id")) {
            this.buttonSetup.enabled = true;
        }
        this.buttonAckTemplateSetup.enabled = this.mainDataSource?.activeRow?.get("func_ack_tid");
        this.setAddCarrierSwitch();
        this.textboxSegAllocCode.visible = this.mainDataSource?.activeRow?.getBoolean("seg_alloc_enabled");
        this.setVisibilityForFields(this.mainDataSource?.activeRow?.get("process_as"));
        this.setButtonCaption();
        this.baseProfile.setCompanyField(this.mainDataSource?.activeRow?.getBoolean("intercompany") && this.baseProfile.isIcEnabled);
        this.setFuncAckTab(this.mainDataSource?.activeRow?.getString("edi_template.format_type") !== this.XML && this.mainDataSource?.activeRow?.getString("edi_template.format_type") !== this.JSON);

        // Set visibility based on direction
        const direction = this.mainDataSource?.activeRow?.get("direction");
        this.setVisibilityByDirection(direction);
        if (this.OUTBOUND === direction) {
            this.layoutInboundFuncAck.mainDataSource.search({
                alt_partner_id: this.mainDataSource?.activeRow?.get("alt_partner_id"),
                partner_id: this.mainDataSource?.activeRow?.get("partner_id"), version: this.mainDataSource?.activeRow?.get("version")
            });
        }
        this.textboxCommId.required = this.OUTBOUND === direction && !this.checkboxIntercompany.checked;
        this.buttonReplySetup.enabled = this.mainDataSource?.activeRow?.get("direction") === this.OUTBOUND && !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("reply_tid"));
        const changeOptions = this.findComponentById("layoutChangeOptions") as ChangeOptions;
        if (this.mainDataSource?.mode !== DataSourceMode.ADD) {
            changeOptions.profileID = this.mainDataSource?.activeRow?.get("id");
        }
        changeOptions.parentDataSource = this.mainDataSource;
        // Set up the parentNeedsSaving callback for changeOptions
        changeOptions.parentNeedsSaving = (needsSaving: boolean) => {
            this.rulesNeedSaving = needsSaving;
        };
        this.buttonAckTemplateSetup.enabled = !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("func_ack_tid"));
        this.textboxCopyFromCompany.visible = !this.baseProfile.isIcEnabled;
    }

    beforeExecution(event: DataSourceExecutionEvent) {
        if (this.mainDataSource?.activeRow?.get("direction") === this.INBOUND && !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("edi_customer_id"))) {
            this.mainDataSource?.activeRow?.set("edi_customer", {
                edi_customer_id: this.mainDataSource?.activeRow?.get("edi_customer_id"),
                edi_customer_name: this.mainDataSource?.activeRow?.get("edi_customer_name"),
                edi_customer_scac: this.mainDataSource?.activeRow?.get("edi_customer_scac"),
                edi_carrier: this.mainDataSource?.activeRow?.get("edi_carrier")
            });
        }
    }

    private async setQuickInfos() {
        const quickInfoMappings: QuickInfoMapping[] = [
            { field: "edi_customer_id", component: this.textboxCustomer, layout: "lme/ar/CustomerQuickInfo" }
        ];

        await this.baseProfile.setQuickInfos(quickInfoMappings, async (field, value, component) => {
            if (field === "edi_customer_id") {
                await this.setEdiCustomerQuickInfo(value, component);
                return true; // Indicates we handled this field
            }
            return false; // Let BaseProfile handle other fields
        });
    }

    private async loadTemplateData() {
        await Promise.all([
            this.sourceEdiTemplateTender.search({ id: this.mainDataSource?.activeRow?.get("template_id") }),
            this.sourceEdiTemplateReply.search({ id: this.mainDataSource?.activeRow?.get("reply_tid") })
        ]);
    }

    private async updateUIVisibility() {
        const direction = this.mainDataSource?.activeRow?.get("direction");
        const isInbound = direction === this.INBOUND;

        this.layoutNotifications.tableEdiNotifyInbound.visible = isInbound;
        this.layoutNotifications.tableEdiNotifyOutbound.visible = !isInbound;
        this.tabFunctionalAcknowledgement.visible = isInbound;
        this.layoutEdiApiUsers.setValues(this.TENDER, this.mainDataSource?.activeRow?.get("id"));
        this.layoutEdiPartnerQualifiers.setPartnerValues(this.mainDataSource?.activeRow?.get("partner_id"), this.mainDataSource?.activeRow?.get("version"));
        this.layoutEdiPartnerQualifiers.setColumnsForTender();
        this.layoutEdiReplyCodePartners.setPartnerValues(this.mainDataSource?.activeRow?.get("reply_partner_id"), this.mainDataSource?.activeRow?.get("reply_version"));
        this.layoutEdiReplyCodePartners.setColumnsForTender();
        this.tabBillTo.visible = !isInbound;
        this.tabCopyTo.visible = isInbound && await this.getCopyControl();
        this.setCompanySettings();
        this.setGeneralTab();
    }

    setupButtonOnClick() {
        this.baseProfile.setupTemplateSlideout("template_id", (returnedValues) => {
            this.updateFromTemplate(returnedValues);
        });
    }

    setupFuncAckButtonOnClick() {
        this.baseProfile.setupTemplateSlideout("func_ack_tid", (returnedValues) => {
            this.updateFuncAckFromTemplate(returnedValues);
        });
    }

    async doAfterModeChange() {
        this.activeRow?.addAfterFieldUpdateListener((event) => {
            this.layoutEdiPartnerQualifiers.setEnabledButtonEnabledProperties(!this.activeRow.hasChanged());
        });
        this.setTitle();
    }

    /** This is an event handler for the onChange event of textboxFuncAckTid. */
    textboxFuncAckTidOnChange(event: ChangeEvent) {
        this.buttonAckTemplateSetup.enabled = !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("func_ack_tid"));
    }

    doBeforeModeChange() {
        this.setTitle();
    }

    setTitle() {
        this.dataHeader.title = (this.mainDataSource?.activeRow?.get("direction") === this.INBOUND ? "Inbound " : "Outbound ") + "Tender Profile";
    }

    updateFromTemplate(updatedValues: EDITemplateUpdateProps) {
        this.baseProfile.updateFromTemplate(updatedValues);
        // Update display after template change
        this.textboxTemplateId.displayData(this.mainDataSource?.activeRow, null, 0);
    }

    setCompanySettings() {
        this.baseProfile.setCompanySettings();
        const isLtl = this.baseProfile.isAssetLtl || this.baseProfile.isBrokerageLtlCompany;
        this.layoutHandlingRequirementXref.visible = isLtl;
        this.layoutPackagingTypeCode.visible = isLtl;
        this.textboxDefMoveType.visible = this.baseProfile.isBoth;
        Api.search("lme/settlement/seg-alloc-control", { company_id: this.mainDataSource?.activeRow?.get("company_id") }).then((result) => {
            if (result) {
                const isActive = result.data && result.data.length > 0 && result.data[0]["is_active"];
                this.layoutAllocationCodeXref.visible = isActive == "Y" && this.mainDataSource?.activeRow?.get("direction") === this.INBOUND;
            }
        });
    }


    updateFuncAckFromTemplate(updatedValues: EDITemplateUpdateProps) {
        const lookupModelRow = new ModelRow(this.textboxFuncAckTid.lookupModel || "lme/datafusion/edi-template", false, {
            id: this.mainDataSource?.activeRow?.get("func_ack_tid"),
            name: updatedValues.templateDescription
        });
        this.mainDataSource?.activeRow?.setLookupModelData("func_ack_tid", lookupModelRow);
        this.textboxFuncAckTid.displayData(this.mainDataSource?.activeRow, null, 0)
        this.mainDataSource?.activeRow?.set("func_version", updatedValues.version);
        if (this.mainDataSource?.activeRow?.get("direction") === this.OUTBOUND) {
            this.mainDataSource?.activeRow?.set("func_partner_id", updatedValues.appSenderId);
            this.mainDataSource?.activeRow?.set("func_altpartner_id", updatedValues.senderId);
        } else {
            this.mainDataSource?.activeRow?.set("func_partner_id", updatedValues.appReceiverId);
            this.mainDataSource?.activeRow?.set("func_altpartner_id", updatedValues.receiverId);
        }
    }

    async masterBatchCheckboxOnChange(event: ChangeEvent) {
        await this.masterBatchOnChange(event);
    }

    private onLookupModelSearch(event: LookupModelSelectionEvent, textbox: Textbox, layout: string) {
        if (event.hasSelection()) {
            if (event.getValueFromSelectedRow("id")) {
                setQuickInfo(event.getValueFromSelectedRow("id"), textbox, layout);
            }
        }
    }

    private commBeforeLookupModelSearch(event: LookupModelSearchEvent, type: string) {
        event.filter.direction_input = this.mainDataSource?.activeRow?.get("direction");
        event.filter.type = type;
    }

    private templateBeforeLookupModelSearch(event: LookupModelSearchEvent, type: string, direction: string) {
        event.filter.direction = direction;
        event.filter.transaction_type = type;
    }

    private setNextBatchFields() {
        if (!this.checkboxFuncUseMBatch.checked) {
            this.textboxFuncNextBatch.visible = true;
            this.labelNextBatchNumber.visible = false;
            this.labelFuncMasterBatch.visible = false;
        } else {
            this.textboxFuncNextBatch.visible = false;
            this.labelNextBatchNumber.visible = true;
            this.labelFuncMasterBatch.visible = true;
        }
    }

    private setGeneralTab() {
        this.textboxOldChangesReply.enabled = this.switchRecentChanges.checked;
    }

    onChangeAutoCancelOrder(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            this.defaultIgnoreMapErrors();
        }
    }

    onChangeAutoCreateOrder(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            this.defaultIgnoreMapErrors();
            this.reconcileAutoCreateOrderWithReply();
            this.setOverrideWarning(event);
        }
    }

    onChangeShowMostRecentChanges(event: ChangeEvent) {
        this.textboxOldChangesReply.enabled = event.newValue === true;
    }

    onChangeCreateLocation(event: ChangeEvent) {
        if (!event.newValue) {
            this.checkboxPartnerCodeLoc.checked = false;
        }
    }

    onChangePartnerCodeLocation(event: ChangeEvent) {
        if (event.newValue) {
            this.switchAutoCreateLocation.checked = true;
        }
    }

    onChangeCreateCarrier(event: ChangeEvent) {
        if (!event.newValue) {
            this.checkboxPartnerCodeCar.checked = false;
        }
    }

    onChangePartnerCodeCarrier(event: ChangeEvent) {
        if (event.newValue) {
            this.switchAutoAddCarrier.checked = true;
        }
    }

    defaultIgnoreMapErrors() {
        if (this.mainDataSource?.activeRow?.getBoolean("auto_create_order")
            || this.mainDataSource?.activeRow?.getBoolean("auto_cancel")
            || this.mainDataSource?.activeRow?.getBoolean("auto_compare")) {
            this.checkboxIgnoreMapErrors.checked = true;
            this.checkboxIgnoreMapErrors.enabled = false;
        } else {
            this.checkboxIgnoreMapErrors.checked = false;
            this.checkboxIgnoreMapErrors.enabled = true;
        }
    }

    reconcileAutoCreateOrderWithReply() {
        if (this.mainDataSource?.activeRow?.getString("direction") === this.INBOUND && this.mainDataSource?.activeRow?.getBoolean("auto_create_order") &&
            (this.mainDataSource?.activeRow?.getBoolean("rply_rquired_orig")
                || this.mainDataSource?.activeRow?.getBoolean("rply_rquired_chang")
                || this.mainDataSource?.activeRow?.getBoolean("rply_rquired_cance"))) {
            this.checkboxAutoCreateReply.checked = true;
        }
    }

    async onChangeAutoCreateReply(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            await this.questionAutoCreateOrderWithoutAutoReply(event);
            this.toggleAutoSendReply(event);
        }
    }

    async questionAutoCreateOrderWithoutAutoReply(event: ChangeEvent) {
        if (this.mainDataSource?.activeRow?.getString("direction") === this.INBOUND && event.userInitiatedChange) {
            const check = event.target as Checkbox;
            if (check.checked) {
                this.layoutReply.checkboxAutoSendReply.checked = true;
                this.checkboxAutoCreateOrder.checked = true;
            } else {
                if (this.checkboxAutoCreateOrder.checked) {
                    CommonDialogs.showYesNo("Auto Create Order is enabled, as well as Requires Reply.  Are you sure you want to disable Auto Create Reply for this trading partner?",
                        "Auto Create Order").then(result => {
                            if (!result) {
                                check.checked = true;
                            }
                        });
                }
            }
        }
    }

    toggleAutoSendReply(event: ChangeEvent) {
        if (event.newValue && !this.mainDataSource?.activeRow?.getBoolean("auto_send_reply")) {
            this.mainDataSource?.activeRow?.set("auto_send_reply", "Y");
        }
    }

    setOverrideWarning(event: ChangeEvent) {
        const companySettings = CompanySettings.get();
        if (companySettings?.is_ltl) {
            if (getDispatchControlBoolean("ltl_calculate_service", false)) {
                if (event.newValue) {
                    this.checkboxOverrideWarning.enabled = true;
                } else {
                    this.checkboxOverrideWarning.enabled = false;
                }
            }
        }
    }

    setAddCarrierSwitch() {
        if (StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("payee_expense_glid"))
            && StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("payee_pay_method"))
            && StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("payee_pay_freq"))) {
            this.switchAddCarrier.checked = false;
            this.setPayeeFieldsVisibility(false);
        } else {
            this.switchAddCarrier.checked = true;
            this.setPayeeFieldsVisibility(true);
        }
    }

    onChangeSwitchAddCarrier(event: ChangeEvent) {
        if (event.newValue) {
            this.textboxPayeeExpenseGlid.visible = true;
            this.textboxPayeePayMethod.visible = true;
            this.textboxPayeePayFreq.visible = true;
            this.setPayeeFieldsVisibility(true);
        } else {
            this.setPayeeFieldsVisibility(false);
        }
    }

    private setPayeeFieldsVisibility(visible: boolean) {
        this.textboxPayeeExpenseGlid.visible = visible;
        this.textboxPayeePayMethod.visible = visible;
        this.textboxPayeePayFreq.visible = visible;
    }

    processAsDropdownSelectionEvent(event: DropdownSelectionEvent) {
        const value = event.selection?.value;
        this.setVisibilityForFields(value);
    }

    private setVisibilityForFields(value: string) {
        if (value === this.TENDER) {
            this.checkboxAutoCreateOrder.visible = true;
            this.checkboxAutoCancel.visible = true;
            this.checkboxAutoCreateReply.visible = true;
            this.checkboxIgnoreMapErrors.visible = true;
            this.switchRecentChanges.visible = true;
            this.textboxOldChangesReply.visible = true;
            this.switchAutoCreateLocation.visible = true;
            this.checkboxPartnerCodeLoc.visible = true;
            this.switchAutoAddCarrier.visible = true;
            this.checkboxPartnerCodeCar.visible = true;
            this.textboxDupCutoffDays.visible = true;
            this.textboxAutoCreateEdiLoc.visible = true;
            this.textboxPromptXrefEdiLoc.visible = true;
            this.checkboxLockMiles.visible = true;
            this.checkboxOverrideWarning.visible = true;
            this.checkboxBlUpdatesOrder.visible = false;
            this.checkboxBlUpdatesOrder.checked = false;
        } else if (value === this.BILL_OF_LADING) {
            this.checkboxAutoCreateOrder.visible = false;
            this.checkboxAutoCreateOrder.checked = false;
            this.checkboxAutoCancel.visible = false;
            this.checkboxAutoCancel.checked = false;
            this.checkboxAutoCreateReply.visible = false;
            this.checkboxIgnoreMapErrors.visible = false;
            this.checkboxIgnoreMapErrors.checked = false;
            this.switchRecentChanges.visible = false;
            this.switchRecentChanges.checked = false;
            this.textboxOldChangesReply.visible = false;
            this.switchAutoCreateLocation.visible = false;
            this.checkboxPartnerCodeLoc.visible = false;
            this.switchAutoAddCarrier.visible = false;
            this.checkboxPartnerCodeCar.visible = false;
            this.textboxDupCutoffDays.visible = false;
            this.textboxAutoCreateEdiLoc.visible = false;
            this.textboxPromptXrefEdiLoc.visible = false;
            this.checkboxLockMiles.visible = false;
            this.checkboxOverrideWarning.visible = false;
            this.checkboxOverrideWarning.checked = false;
            this.checkboxBlUpdatesOrder.visible = true;
        }
    }
    /** This is an event handler for the beforeExecution event of sourceAgencyLink. */
    sourceAgencyLinkBeforeExecution(event: DataSourceExecutionEvent) {
        const activeRow = this.mainDataSource?.activeRow;
        if (activeRow) {
            event.filter.primary_agency = activeRow.get("primary_agency");
        }
    }

    /** This is an event handler for the onValidate event of sourceEdiorderProfileDisplay. */
    async sourceEdiorderProfileDisplayOnValidate(event: DataSourceValidationEvent) {
        const agencyRows = this.layoutAgencyLink.tableAgencyLink.rowCount;
        // Check if at least one agency is set as primary
        const hasPrimary = this.layoutAgencyLink.tableAgencyLink.data.some(row => row.get("is_primary") === "Y");
        if ((agencyRows === 0 || !hasPrimary) && this.mainDataSource?.activeRow) {
            this.mainDataSource?.activeRow?.set("primary_agency", "");
        }
        if (this.mainDataSource?.activeRow?.get("direction") === this.OUTBOUND && this.checkboxIntercompany.checked) {
            this.mainDataSource?.activeRow?.set("transmit_method", "X");
        }
        if (this.rulesNeedSaving) {
            await this.postAllExpandedLayoutDatasources();
            this.sourceEdiReqdfldgroup.post();//TODO: is this necessary, if not -- we can remove the post when the active switch is clicked. I thnk it's needed to save the grandchild datasources
        }
        if (this.mainDataSource?.activeRow?.get("direction") === this.OUTBOUND && this.mainDataSource?.activeRow?.getBoolean("requires_reply") && StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("reply_tid"))) {
            event.validationResults.push({
                isValid: false,
                component: this.textboxReplyTemplateId,
                caption: "Reply template required when requires reply is true.",
                validationMessage: "Reply template is required when requires reply is true."
            });
        }
    }

    setButtonCaption() {
        if (!StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("edi_customer_id"))) {
            this.buttonCustomer.caption = "Update";
            this.buttonRemoveCustomer.visible = true;
        } else {
            this.buttonCustomer.caption = "Add New";
            this.buttonRemoveCustomer.visible = false;
        }
    }

    onClickCustomerButton() {
        const layout = Layout.getLayout("lme/datafusion/EdiCustomerSlideout") as EdiCustomerSlideout;
        const modelRow = new ModelRow("lme/datafusion/edi-customer", true, {
            partner_id: this.mainDataSource?.activeRow?.get("partner_id"),
            alt_partner_id: this.mainDataSource?.activeRow?.get("alt_partner_id"),
            direction: this.mainDataSource?.activeRow?.get("direction"),
            version: this.mainDataSource?.activeRow?.get("version"),
            transaction_type: "T",
            transaction_set: this.mainDataSource?.activeRow?.get("transaction_set"),
            scac: this.mainDataSource?.activeRow?.get("edi_customer_scac"),
            carrier: this.mainDataSource?.activeRow?.get("edi_carrier"),
            customer_id: this.mainDataSource?.activeRow?.get("edi_customer_id"),
        });
        modelRow.setLookupModelData("customer_id", { id: this.mainDataSource?.activeRow?.get("edi_customer_id"), name: this.mainDataSource?.activeRow?.get("edi_customer_name") });
        new EditRowDecorator({
            title: "Update Customer",
            layout: layout,
            data: modelRow,
            width: '25%',
            overlayProps: { greyedBackground: true, closeOnClickOff: true },
            saveButtonProps: {
                caption: "Save & Close",
                enabled: true,
                visible: true
            },
            onSave: async (updatedData) => {
                this.mainDataSource?.activeRow?.set("edi_customer_scac", layout.textboxScac.text ?? "");
                this.mainDataSource?.activeRow?.set("edi_carrier", layout.textboxCarrier.text ?? "");
                this.mainDataSource?.activeRow?.set("edi_customer_id", layout.textboxCustomerId.getFirstLookupModelData()?.get("id"));
                this.mainDataSource?.activeRow?.set("edi_customer_name", layout.textboxCustomerId.getFirstLookupModelData()?.get("name"));
                this.mainDataSource?.activeRow?.set("edi_customer", {
                    edi_customer_id: layout.textboxCustomerId.getFirstLookupModelData()?.get("id"),
                    edi_customer_scac: layout.textboxScac.text ?? "",
                    edi_carrier: layout.textboxCarrier.text ?? ""
                });
                await this.setEdiCustomerQuickInfo(this.mainDataSource?.activeRow?.get("edi_customer_id"), this.textboxCustomer);
                this.setButtonCaption();
            }
        });
    }

    async onClickRemoveCustomerButton() {
        const yesNo = await CommonDialogs.showYesNo("Are you sure you want to remove this customer record?");
        if (yesNo) {
            this.mainDataSource?.activeRow?.set("edi_customer", null);
            this.mainDataSource?.activeRow?.set("edi_customer_scac", null);
            this.mainDataSource?.activeRow?.set("edi_carrier", null);
            this.mainDataSource?.activeRow?.set("edi_customer_id", null);
            this.setButtonCaption();
        }
    }

    async setEdiCustomerQuickInfo(value: string, component: Textbox) {
        const customerModelRow = await new ModelCustomer().searchSingle({ id: value });
        if (customerModelRow !== undefined && customerModelRow !== null) {
            if (!StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("edi_customer_scac"))) {
                customerModelRow.set("edi_customer_scac_visible", true);
                customerModelRow.set("edi_customer_scac", this.mainDataSource?.activeRow?.get("edi_customer_scac"));
            }
            if (!StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("edi_carrier"))) {
                customerModelRow.set("edi_customer_carrier_visible", true);
                customerModelRow.set("edi_customer_carrier", this.mainDataSource?.activeRow?.get("edi_carrier"));
            }
            CommonTooltips.setTooltipContentLayout(
                component,
                customerModelRow,
                "lme/ar/CustomerQuickInfo"
            );
            component.tooltipDynamicOptions = { position: Alignment.BOTTOM };
        }
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.edi.EntryEdiOrderProfile", "<ext> search id ='" + this.mainDataSource?.activeRow?.get("id") + "'");
    }

    onChangeIntercompany(event: ChangeEvent) {
        this.baseProfile.handleIntercompanyChange(event, (enabled) => {
            this.baseProfile.setCompanyField(enabled);
            const isRequired = !enabled && this.mainDataSource?.activeRow?.get("direction") === this.OUTBOUND;
            this.textboxCommId.required = isRequired;
        });
    }

    private setFuncAckTab(isVisible: boolean) {
        this.tabFunctionalAcknowledgement.visible = isVisible;
    }

    private setVisibilityByDirection(direction: string) {
        this.tabCompare.visible = direction !== this.OUTBOUND;
        this.panelInboundGeneral.visible = direction === this.INBOUND;
        this.panelOutboundGeneral.visible = direction === this.OUTBOUND;
        this.layoutReply.visible = direction === this.INBOUND;
        this.panelInboundReply.visible = direction === this.OUTBOUND;
        this.tabCarrier.visible = direction === this.OUTBOUND;
        this.tabChangeOptions.visible = direction === this.OUTBOUND;
        this.tabDataValidation.visible = direction === this.OUTBOUND;
        this.tabAutoCreate.visible = direction === this.INBOUND;
        this.layoutRevenueCode.visible = direction === this.INBOUND;
        this.layoutOrderTypeXref.visible = direction === this.INBOUND;
        this.layoutAllocationCodeXref.visible = direction === this.INBOUND;
        this.layoutHandlingRequirementXref.visible = (this.baseProfile.isAssetLtl || this.baseProfile.isBrokerageLtlCompany) && direction === this.INBOUND;
        this.layoutPackagingTypeCode.visible = (this.baseProfile.isAssetLtl || this.baseProfile.isBrokerageLtlCompany) && direction === this.INBOUND;
        this.layoutChargeCode.visible = direction === this.INBOUND;
        this.panelOutboundFuncAck.visible = direction === this.INBOUND;
        this.panelInboundFuncAck.visible = direction === this.OUTBOUND;
        this.layoutDeductCodeXref.mainLabelTitle = direction === this.INBOUND ? "Deductions" : "Earnings";
        this.layoutDeductCodeXref.tableDeductions.columns[1].headingCell.caption = direction === this.INBOUND ? "Deductions" : "Earnings";
    }


    /** This is an event handler for the onRowExpand event of tableReqdFldGrp. */
    tableReqdFldGrpOnRowExpand(event: TableRowExpansionEvent) {
        //lme/datafusion/EdiReqdFldGrpExpanded
        const tableRow: TableRow = event.target as TableRow;
        const expandPanel = event.expandComponentParent as Panel;
        const modelRow: ModelRow = tableRow.data
        const layoutToExpand: EdiReqdFldGrpExpanded = expandPanel.findComponentById("layoutEdiRequiredFieldsExpanded") as EdiReqdFldGrpExpanded;

        // Set the private variables in the expanded layout
        if (layoutToExpand) {
            layoutToExpand.version = this.mainDataSource?.activeRow?.get("version");
            layoutToExpand.partnerId = this.mainDataSource?.activeRow?.get("partner_id");
            layoutToExpand.altPartnerId = this.mainDataSource?.activeRow?.get("alt_partner_id");
            layoutToExpand.parentDataSource = this.sourceEdiReqdfldgroup;
            layoutToExpand.groupId = modelRow.get("id");
            layoutToExpand.transactionType = "204";

            // Pass the rulesNeedSaving setter as a callback to the expanded layout
            layoutToExpand.parentNeedsSaving = (needsSaving: boolean) => {
                this.rulesNeedSaving = needsSaving;
            };
        }

        layoutToExpand?.mainDataSource.search({ group_id: modelRow.get("id") });
    }

    /** This is an event handler for the onRowDisplay event of tableReqdFldGrp. */
    tableReqdFldGrpOnRowDisplay(event: TableRowDisplayEvent) {
        const textSequence = event.getTableRow().findComponentById("textboxRuleSequence") as Textbox;
        if (textSequence.text === "0") {
            textSequence.required = false;
            textSequence.visible = false;
        }
    }

    /** This is an event handler for the afterTableRowMove event of tableReqdFldGrp. */
    tableReqdFldGrpAfterTableRowMove(event: TableRowMoveEvent) {
        // Update sequence field in the data to match the new row order
        this.tableReqdFldGrp.allRows.forEach((row, index) => {
            row.data.set("sequence", index + 1);
        });
        this.mainDataSource.mode = DataSourceMode.UPDATE;
    }

    /** This is an event handler for the onRowCreate event of tableReqdFldGrp. */
    tableReqdFldGrpOnRowCreate(event: TableRowCreationEvent) {
        const row = event.target as TableRow;
        const textSequence = row.findComponentById("textboxRuleSequence") as Textbox;
        if (row) {
            if (row.data.get("sequence") === undefined || row.data.get("sequence") === null) {
                row.data.set("sequence", 0);
                if (textSequence) {
                    textSequence.required = false;
                    textSequence.visible = false;
                }
            }

        }
    }

    /** This is an event handler for the afterModeChange event of sourceEdiReqdfldgroup. */
    sourceEdiReqdfldgroupAfterModeChange(event: DataSourceModeChangeEvent) {
        // Only mark as dirty if there are actual changes, not just mode changes during initialization
        if (event.newMode === DataSourceMode.UPDATE || event.newMode === DataSourceMode.ADD) {
            // Check if this datasource actually has changes before marking the main datasource as dirty
            if (this.sourceEdiReqdfldgroup.hasChanged()) {
                //    this.rulesNeedSaving = true;//TODO This needs to be set, butnot here
                this.mainDataSource.notifyHasChangedComponents(true);
            }
        }
    }


    /**
     * Loops through all tableReqdFldGrp table rows, gets their expanded panels,
     * collects all rows from expanded layouts, and posts them together.
     */
    async postAllExpandedLayoutDatasources(): Promise<void> {
        try {
            // Get all table rows from tableReqdFldGrp
            const allRows = this.tableReqdFldGrp.allRows;
            const allDataRowsToPost = [];

            // First pass: collect all rows from expanded panels
            for (const tableRow of allRows) {
                // Check if the row is expanded
                if (tableRow.expanded) {
                    // Get the expanded panel
                    const expandedPanel = tableRow.getExpansionComponent() as Panel;

                    if (expandedPanel) {
                        // Find the EdiReqdFldGrpExpanded layout within the expanded panel
                        const expandedLayout = expandedPanel.findComponentById("layoutEdiRequiredFieldsExpanded") as EdiReqdFldGrpExpanded;

                        if (expandedLayout && expandedLayout.mainDataSource) {
                            // Collect all data rows from this expanded layout
                            const dataArray = expandedLayout.mainDataSource.data;

                            // Add all rows to our collection array
                            allDataRowsToPost.push(...dataArray);
                        } else {
                            console.warn(`No expandedLayout or mainDataSource found for expanded row with group_id: ${tableRow.data.get("id")}`);
                        }
                    } else {
                        console.warn(`No expandedPanel found for expanded row with group_id: ${tableRow.data.get("id")}`);
                    }
                }
            }

            // Second pass: post all collected rows together
            if (allDataRowsToPost.length > 0) {
                try {
                    for (const row of allDataRowsToPost) {
                        await row.post();
                    }
                } catch (error) {
                    console.error(`Failed to post collected rows from expanded layouts`, error);
                }
            }

            // Third pass: ensure all expanded panels stay expanded
            for (const tableRow of allRows) {
                if (tableRow.expanded) {
                    const expandedPanel = tableRow.getExpansionComponent() as Panel;
                    if (expandedPanel) {
                        expandedPanel.expand();
                    }
                }
            }

        } catch (error) {
            console.error("Error posting expanded layout datasources:", error);
            throw error;
        }
    }

    /** This is an event handler for the onClick event of switchActive. */
    async switchActiveOnClick(event: ClickEvent) {
        await this.sourceEdiReqdfldgroup.post();
    }

    /** This is an event handler for the onRowCreate event of tableEdiCarrier. */
    tableEdiCarrierOnRowCreate(event: TableRowCreationEvent) {
        this.baseProfile.handleEdiCustomerRowCreate(event, "T");
    }

    /** This is an event handler for the onClick event of buttonFilenameCustomize. */
    buttonFilenameCustomizeOnClick(event: ClickEvent) {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomFilename",
            "Customize Functional Acknowledgement Filename",
            this.mainDataSource,
            "func_ack_filename",
            "fileMask",
            false,
            this.mainDataSource?.activeRow?.get("func_next_batch") ?? 0
        );
    }


    /** This is an event handler for the onClick event of buttonTransactionCustomize. */
    buttonTransactionCustomizeOnClick(event: ClickEvent) {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomTransControl",
            "Customize Functional Acknowledgement Transmission Control Number",
            this.mainDataSource,
            "func_ack_st02_frmt",
            "mask",
            false,
            this.mainDataSource?.activeRow?.get("func_next_batch") ?? 0
        );
    }

    /** This is an event handler for the onRowCreate event of tableEdiCustomer. */
    tableEdiCustomerOnRowCreate(event: TableRowCreationEvent) {
        this.baseProfile.handleEdiCustomerRowCreate(event, "T");
    }

    /** This is an event handler for the onClick event of buttonCustomFilename. */
    buttonCustomFilenameOnClick(event: ClickEvent) {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomFilename",
            "Customize Tender Filename",
            this.mainDataSource,
            "filename",
            "fileMask",
            false,
            this.mainDataSource?.activeRow?.get("next_batch") ?? 0
        );
    }

    /** This is an event handler for the onClick event of buttonCustomizeTransControNoi. */
    buttonCustomizeTransControNoiOnClick(event: ClickEvent) {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomTransControl",
            "Customize Tender Transmission Control Number",
            this.mainDataSource,
            "st02_format",
            "mask",
            false,
            this.mainDataSource?.activeRow?.get("next_batch") ?? 0
        );
    }

    /** This is an event handler for the onClick event of buttonReplySetup. */
    buttonReplySetupOnClick(event: ClickEvent) {
        this.baseProfile.setupTemplateSlideout("reply_tid", (returnedValues) => {
            this.updateReplyFromTemplate(returnedValues);
        });
    }
    updateReplyFromTemplate(updatedValues: EDITemplateUpdateProps) {
        const lookupModelRow = new ModelRow(this.textboxTemplateId.lookupModel || "lme/datafusion/edi-template", false, {
            id: this.mainDataSource?.activeRow?.get("template_id"),
            name: updatedValues.templateDescription
        });
        this.mainDataSource?.activeRow?.setLookupModelData("reply_tid", lookupModelRow);
        this.textboxReplyTemplateId.displayData(this.mainDataSource?.activeRow, null, 0)
        this.mainDataSource?.activeRow?.set("reply_version", updatedValues.version);
        this.mainDataSource?.activeRow?.set("reply_partner_id", updatedValues.appSenderId);
        this.mainDataSource?.activeRow?.set("reply_altpartnerid", updatedValues.senderId);
    }

    /**
         * Sets the tooltip for the auto send tender textbox based on the selected value
         * @param textbox The textbox component to set the tooltip on
         * @param selectedValue The selected dropdown value ("Y" for auto-send, other values for manual)
         */
    private setAutoSendTenderTooltip(textbox: Textbox, selectedValue: string) {
        if (selectedValue && selectedValue === "CRAS") {
            textbox.tooltip = "Tender will be automatically sent at dispatch.  Original and cancels will auto send."
        } else if (selectedValue && selectedValue === "NONE") {
            textbox.tooltip = "User will be prompted at dispatch to send, but not sent automatically.  This applies to original and cancel tenders.";
        } else {
            textbox.tooltip = "Tender will be sent for each partner type delivery when movement to partner departs.  Original and cancels will auto send.";
        }
    }

    /** This is an event handler for the afterDropdownSelection event of textboxAutoSendTender. */
    textboxAutoSendTenderAfterDropdownSelection(event: DropdownSelectionEvent) {
        const textbox = event.target as Textbox;
        const selectedValue = event.selection?.value;
        this.setAutoSendTenderTooltip(textbox, selectedValue);
    }

    /** This is an event handler for the onRowCreate event of tableRequiredField. */
    tableRequiredFieldOnRowCreate(event: TableRowCreationEvent) {
        this.baseProfile.handleRequiredFieldRowCreate(event, "204", "O");
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxEDiCarrier. */
    textboxEDiCarrierBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target);
        const carrierTextBox = tableRow?.findComponentById("textboxEDiCarrier") as Textbox;
        event.filter.status = "A";
        if (carrierTextBox) {
            event.filter.lm_search = carrierTextBox.text;
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxEDiCarrier. */
    async textboxEDiCarrierOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (!event.hasSelection()) return;
        const selectedCarrierId = event.getValueFromSelectedRow("id");
        if (StringUtil.isEmptyString(selectedCarrierId)) return;

        const existingRecords = await new ModelEdiCarrier().search({
            carrier_id: selectedCarrierId,
            direction: this.OUTBOUND,
            transaction_type: this.TENDER
        });

        const currentProfileId = this.mainDataSource?.activeRow?.get("id");
        const isDuplicate = existingRecords?.modelRows?.some(
            row => row.get("id") !== currentProfileId
        );

        if (isDuplicate) {
            const textbox = event.target as Textbox;
            const tableRow = TableRow.getContainingTableRow(textbox);
            textbox.clear();
            if (tableRow) {
                this.tableEdiCarrier.deleteRow(tableRow);
            }
            CommonDialogs.showError("This carrier is already in use by another Load Tender Profile.");
        }
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxReplyTemplateId. */
    textboxReplyTemplateIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.transaction_type = "R";
        event.filter.direction = "I";
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxReplyCommId. */
    textboxReplyCommIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.direction = "I";
        event.filter.comm_status = 'A';
    }

    /** This is an event handler for the onChange event of textboxReplyTemplateId. */
    textboxReplyTemplateIdOnChange(event: ChangeEvent) {
        this.buttonReplySetup.enabled = !StringUtil.isEmptyString(this.textboxReplyTemplateId.text);
    }

    /** This is an event handler for the onChange event of checkboxMasterBatch. */
    async masterBatchFuncCheckboxOnChange(event: ChangeEvent) {
        await this.masterBatchOnChange(event);
    }

    private async masterBatchOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const check = event.target as Checkbox;
            const partnerName = this.mainDataSource.activeRow.get("partner_name");
            const partnerID = this.mainDataSource.activeRow.get("partner_id");
            const altPartnerId = this.mainDataSource.activeRow.get("alt_partner_id");
            const version = this.mainDataSource.activeRow.get("version");

            await handleMasterBatchCheck(check, partnerName, partnerID, altPartnerId, version);
        }
    }

    /** This is an event handler for the onClick event of buttonMasterBatch. */
    async buttonMasterBatchOnClick(event: ClickEvent) {
        const partnerName = this.mainDataSource.activeRow.get("partner_name");
        const partnerID = this.mainDataSource.activeRow.get("partner_id");
        const altPartnerId = this.mainDataSource.activeRow.get("alt_partner_id");
        const version = this.mainDataSource.activeRow.get("version");

        await handleButtonMasterBatchClick(partnerName, partnerID, altPartnerId, version, () => {
            this.checkboxMasterBatch.checked = true;
        });
    }

    /** This is an event handler for the onLookupModelSelection event of textboxReplyTemplateId. */
    async textboxReplyTemplateIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (!StringUtil.isEmptyString(event.getValueFromSelectedRow("id"))) {
            const template = await new ModelEdiTemplate().searchSingle({ id: event.getValueFromSelectedRow("id") });
            if (template) {
                setReplyTemplateValues(template, this.mainDataSource);
            }
        }
    }

    /** This is an event handler for the onClick event of buttonFAMasterBatch. */
    async buttonFAMasterBatchOnClick(event: ClickEvent) {
        const partnerName = this.mainDataSource.activeRow.get("partner_name");
        const partnerID = this.mainDataSource.activeRow.get("partner_id");
        const altPartnerId = this.mainDataSource.activeRow.get("alt_partner_id");
        const version = this.mainDataSource.activeRow.get("version");

        await handleButtonMasterBatchClick(partnerName, partnerID, altPartnerId, version, () => {
            this.checkboxFuncUseMBatch.checked = true;
        });
    }

    /** This is an event handler for the onLookupModelSelection event of textboxCommId. */
    commIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const textbox = event.target as Textbox;
        const field = textbox.field;
        if (this.mainDataSource?.activeRow.get("direction") === "O") {
            handleCommIdSelection(field, textbox, event.hasSelection(), this.mainDataSource?.activeRow, "204");
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxFuncCommId. */
    textboxFuncCommIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const textbox = event.target as Textbox;
        const field = textbox.field;
        handleCommIdSelection(field, textbox, event.hasSelection(), this.mainDataSource?.activeRow, "997");
    }

    ruleDescriptionOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange && event.newValue !== event.oldValue) {
            const textBox = event.target as Textbox;
            const modelRow = textBox.getRelevantModelRow();
            if (modelRow) {
                modelRow.set("descr", event.newValue);
            }
        }
    }

    /** This is an event handler for the onChange event of textboxTemplateId. */
    textboxTemplateIdOnChange(event: ChangeEvent) {
        this.buttonSetup.enabled = !StringUtil.isEmptyString(this.textboxTemplateId.text);
    }

    async profileExists(profileId: string, altPartnerId: string, version: string, companyId: string, direction: string, transactionSet: string): Promise<boolean> {
        const profile = await new ModelEdiorderProfile().searchSingle({
            partner_id: profileId,
            alt_partner_id: altPartnerId,
            version: version,
            company_id: companyId,
            direction: direction,
            transaction_set: transactionSet
        });
        return profile !== null && profile !== undefined;
    }

    beforeCompanyIdLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.copy_to = true;
    }

    companyCopyToOnRowCreate(event: TableRowCreationEvent) {
        const row = event.target as TableRow;
        const activeRow = this.mainDataSource?.activeRow;
        if (!activeRow) return;
        row.data.set("alt_partner_id", activeRow.get("alt_partner_id"));
        row.data.set("partner_id", activeRow.get("partner_id"));
        row.data.set("version", activeRow.get("version"));
        row.data.set("direction", activeRow.get("direction"));
        row.data.set("transaction_set", activeRow.get("transaction_set"));
    }

    async onSelectDestinationCompany(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const textbox = event.target as Textbox;
            const tableRow = TableRow.getContainingTableRow(event.target);
            const newRow = tableRow.data as ModelRow;

            const newText = textbox.text;
            if (!tableRow || !textbox || StringUtil.isEmptyString(newText)) return;
            const isDuplicate = this.tableCopyTo.data.find((row) =>
                row.get("destination_company") === newText && row !== newRow
            );

            if (isDuplicate) {
                await CommonDialogs.showError("An entry already exists for this company");
                textbox.clear();
            }
        }
    }

    async getCopyControl(): Promise<boolean> {
        const response = await new ModelCopyControl().searchSingle({ copy_type: "C" });
        return response !== null && response !== undefined;
    }
}
